import { AllDrum, AllMelodic, MAX_STEM_COUNT } from '@arpeggi/shared'
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk'
import { observer } from 'mobx-react-lite'
import React, { FC, forwardRef, MutableRefObject, RefObject } from 'react'
import { useDrop } from 'react-dnd'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { GRAY_COLORS, styled } from '../../lib/stitches'
import { useTreeState } from '../../stores/Root'
import { getRandomSampleFromCategoryList } from '../../stores/song/sample/SampleUtils'
import { undoManager } from '../../stores/StudioStore'
import { Column, Row, VerticalCenteredRow } from '../core/Components'
import { AudioRectangleIcon, MidiRectangleIcon, PlusIcon } from '../core/icons'
import withTooltip from '../core/withTooltip'
import { blue5, gray2, gray6 } from '../platform/color'
import { segmentStudioButton, StudioDraggableTypes } from './StudioUtils'

const useStemAndSampleDropzone = () => {
  const flags = useFlags()
  const { studio } = useTreeState()

  //////////////// INSTRUMENT //////////////////

  const onClickMidi = () => {
    segmentStudioButton('Randomize', 'track table')
    if (!ArpRegistry.readyForStudioUse) return

    let sampleIndex = Number(ArpRegistry.getRandomSampleId())
    // if there's already a drum kit, we want to select an melodic instrument
    // so as not to confuse the user
    if (studio.song.hasDrumKit) {
      while (AllDrum.includes(ArpRegistry.retrieveFromCache(sampleIndex).category)) {
        sampleIndex = Number(ArpRegistry.getRandomSampleId())
      }
    }
    studio.addNewStemForSample(sampleIndex)
  }

  // For dropping a new sample or stem.
  const [{ isOver: isOverInstrumentDrop }, instrumentDrop] = useDrop(
    () => ({
      accept: [StudioDraggableTypes.SAMPLE, StudioDraggableTypes.STEM],
      drop: (item: any) => {
        if (item.type === StudioDraggableTypes.STEM) {
          console.log('dropped a new stem')
          studio.addStemFromRemix(item.stem)
        } else if (item.type === StudioDraggableTypes.SAMPLE) {
          console.log('dropped a new sample: ', item.sample)
          studio.addNewStemForSample(item.sample)
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    []
  )

  ////////////// AUDIO /////////////////////

  const onClickAudio = () => studio.addAudioStem()

  const acceptedAudioStemTypes = [StudioDraggableTypes.SAMPLE]
  if (flags.stemRemixingAsClips) {
    acceptedAudioStemTypes.push(StudioDraggableTypes.STEM)
  }
  // For dropping a new sample
  const [{ isOver: isOverAudioDrop }, audioDrop] = useDrop(
    () => ({
      accept: acceptedAudioStemTypes,
      drop: (item: any) => {
        if (item.type === StudioDraggableTypes.SAMPLE) {
          console.log('dropped a new sample: ', item.sample)
          undoManager.startGroup(() => {
            studio.disableAudioUpdates()
            studio.addAudioStem()
            studio.addClipFromSampleToAudioStem(item.sample, studio.song.stems.length - 1, 0)
            studio.enableAudioUpdates()
            undoManager.stopGroup()
          })
        } else if (item.type === StudioDraggableTypes.STEM) {
          console.log('dropped a new stem as audio clip: ', item.stem.id)
          studio.addAudioStemFromStem(item.stem.id)
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    [acceptedAudioStemTypes]
  )

  return {
    onClickMidi,
    onClickAudio,
    isOverInstrumentDrop,
    instrumentDrop,
    isOverAudioDrop,
    audioDrop
  }
}

interface DropzoneProps {
  icon: JSX.Element
  dropzoneActivated?: boolean
  noBorderLeft?: boolean
  onClick?: () => void
}
const Dropzone = forwardRef(
  (
    { icon, noBorderLeft, onClick, dropzoneActivated }: DropzoneProps,
    ref: MutableRefObject<HTMLDivElement>
  ) => {
    return (
      <Row
        onClick={onClick}
        ref={ref}
        css={{
          flex: 1,
          cursor: onClick && 'pointer',
          height: '100%',
          borderRadius: '5px',
          '&:hover': {
            backgroundColor: gray2
          },
          border: dropzoneActivated && `1px dashed ${blue5}`,
          backgroundColor: dropzoneActivated && '#F6F9FE'
        }}
        verticalCenter
        horizontalCenter
      >
        <PlusIcon height="19px" width="19px" style={{ marginRight: '8px' }} />
        {icon}
      </Row>
    )
  }
)

const TooltipWrapper = withTooltip(Row)

const DropSecondaryText = styled(VerticalCenteredRow, {
  color: gray6,
  fontSize: '12px',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline'
  }
})

interface OldStemAndSampleDropzoneProps {
  height: string
  onClick: () => void
  isDropHoverOver: boolean
}
const OldStemAndSampleDropzone = observer(
  React.forwardRef(
    (
      { height, isDropHoverOver, onClick }: OldStemAndSampleDropzoneProps,
      ref: RefObject<HTMLDivElement>
    ) => {
      const { studio } = useTreeState()
      const maxStemsReached = studio.song.stems.length >= MAX_STEM_COUNT

      const onRandomize = (e) => {
        segmentStudioButton('Randomize', 'track table')
        e.stopPropagation()
        if (!ArpRegistry.readyForStudioUse) return

        let sampleIndex: number
        if (studio.song.hasDrumKit) {
          sampleIndex = getRandomSampleFromCategoryList(AllMelodic).sampleIndex
        } else {
          sampleIndex = ArpRegistry.getRandomSampleId()
        }
        studio.addNewStemForSample(sampleIndex)
      }

      return (
        <Column
          className="unselectable fw-medium"
          style={{
            height: height,
            minHeight: height,
            width: '100%',
            minWidth: '100%',
            backgroundColor: maxStemsReached
              ? GRAY_COLORS.gray03
              : isDropHoverOver
              ? GRAY_COLORS.gray005
              : GRAY_COLORS.gray01,
            borderBottom: '1px solid var(--studio-medium)',
            textAlign: 'center'
          }}
          ref={!maxStemsReached ? ref : null}
          onClick={onClick}
          horizontalCenter
          verticalCenter
        >
          {!maxStemsReached && (
            <>
              Drop samples or stems
              <DropSecondaryText onClick={onRandomize}>
                or randomize
                <img
                  src="/static/icons/studio_magic_wand_gray8.svg"
                  style={{ paddingLeft: '5px' }}
                />
              </DropSecondaryText>
            </>
          )}
        </Column>
      )
    }
  )
)

interface StemAndSampleDropzoneProps {
  height: string
}
export const StemAndSampleDropzone: FC<StemAndSampleDropzoneProps> = withLDConsumer()(
  observer(({ height }) => {
    const flags = useFlags()
    const {
      onClickMidi,
      onClickAudio,
      isOverInstrumentDrop,
      instrumentDrop,
      isOverAudioDrop,
      audioDrop
    } = useStemAndSampleDropzone()
    const { studio } = useTreeState()
    const maxStemsReached = studio.song.stems.length >= MAX_STEM_COUNT

    return (
      <Row
        css={{
          borderBottom: flags.clipsmode && `1px solid var(--studio-medium)`,
          height: height,
          minHeight: height
        }}
        verticalCenter
      >
        {!maxStemsReached && (
          <>
            {!flags.clipsmode ? (
              // TODO: Remove this once clipsmode is fully supported
              <OldStemAndSampleDropzone
                height="100%"
                onClick={onClickMidi}
                ref={!maxStemsReached ? instrumentDrop : null}
                isDropHoverOver={isOverInstrumentDrop}
              />
            ) : (
              <>
                <TooltipWrapper
                  tooltipText="Add Instrument Track"
                  css={{ flex: 1, height: '100%' }}
                >
                  <Dropzone
                    icon={<MidiRectangleIcon width="45px" height="24px" />}
                    onClick={onClickMidi}
                    ref={!maxStemsReached ? instrumentDrop : null}
                    dropzoneActivated={isOverInstrumentDrop}
                  />
                </TooltipWrapper>
                <TooltipWrapper
                  tooltipText="Add Audio Track"
                  css={{ flex: 1, height: '100%', borderLeft: `1px solid var(--studio-medium)` }}
                >
                  <Dropzone
                    icon={<AudioRectangleIcon width="45px" height="24px" />}
                    onClick={onClickAudio}
                    ref={!maxStemsReached ? audioDrop : null}
                    dropzoneActivated={isOverAudioDrop}
                  />
                </TooltipWrapper>
              </>
            )}
          </>
        )}
      </Row>
    )
  })
)

export default Dropzone
